import type { ShotSuggestion, StoryboardShot } from '../../services/studioService';

/**
 * MERGE A CREW SUGGESTION INTO A SHOT.
 *
 * Pulled out of the panel because it is the whole point of asking for help and it is where the mistakes are —
 * a merge that takes too little wastes what was proposed, and a merge that takes too much overwrites what the
 * creator already decided. Neither is visible in a screenshot, so it wants its own tests rather than a walk
 * through the inspector.
 *
 * TWO RULES, and everything here follows from them.
 *
 *   1. **Only what was actually proposed is applied.** A copilot that stayed quiet about sound is not saying
 *      "silence" — it is saying nothing. Clearing the creator's own sound because the answer omitted a field
 *      would make asking for help destructive, and nobody would ask twice.
 *
 *   2. **The copilot writes the words; casting stays the creator's.** A proposed line keeps whatever voice the
 *      creator had already cast for that same speaker, so re-asking for a better line never silently recasts
 *      the character (owner law: a voice is a decision, not a detail).
 */
export function applySuggestion(shot: StoryboardShot, suggestion: ShotSuggestion): StoryboardShot {
  return {
    ...shot,
    // An empty line means the copilot refined nothing, not that the shot is now blank.
    line: suggestion.line || shot.line,
    toggles: { ...shot.toggles, ...suggestion.toggles },
    ...(suggestion.camera ? { camera: suggestion.camera } : {}),
    ...(suggestion.weight ? { weight: suggestion.weight } : {}),
    ...(suggestion.sfx ? { sfx: suggestion.sfx } : {}),
    ...(suggestion.dialogue ? { dialogue: suggestion.dialogue.map((d) => withCastVoice(shot, d)) } : {}),
    // WHAT HAPPENS WHEN, inside the clip. The copilot now proposes it under the same house template the
    // director works to, and it reaches the render's prompt — a five-second shot that says nothing about
    // timing is paced by the model, which spends four seconds on a look (owner 2026-09-13).
    ...(suggestion.timeline?.length ? { timeline: suggestion.timeline } : {}),
  };
}

/** Carry forward the voice already cast for this speaker, matched by name. */
function withCastVoice(shot: StoryboardShot, line: { character?: string; line: string }) {
  const already = (shot.dialogue || []).find((d) => d.character && line.character && d.character === line.character);
  return { character: line.character, line: line.line, ...(already?.voiceId ? { voiceId: already.voiceId } : {}) };
}

export default applySuggestion;
